import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_CURRENCY,
  paymentMethods,
  qualifiesForFreeShipping,
} from "../../utils/global_const";
import InvoiceProduct from "./InvoiceProduct";
import styled from "styled-components";
import { ProductContext } from "../../context/ProductContext";
import { AuthUserContext } from "../../context/AuthUserContext";
import { jwtDecode } from "jwt-decode";
import TagManager from "react-gtm-module";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";
import { useTranslation } from "react-i18next";
import CreditCardButton from "../stripe/CreditCardButton";
import CardPaymentLink from "../mollie/CardPaymentLink";
import { cartActions } from "../../store/cart-slice";
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionBody,
} from "../checkout/CheckoutSection";
import { getProductTitle } from "../../utils/productDisplayName";

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const P = styled.p`
  padding: var(--spacing-xs) 0;
  margin: 0;
  color: var(--text-200);
  font-size: var(--font-size-small);
`;

const TotalRow = styled(FlexDiv)`
  border-top: 1px solid var(--bg-200);
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-sm);

  h2 {
    margin: 0;
    font-size: var(--font-size-large);
    color: var(--text-100);
  }
`;

const Submit = styled.button`
  width: 100%;
  margin: var(--spacing-md) 0 0;

  opacity: ${(props) =>
    props.disabled ? 0.5 : 1}; /* Stilizacija za disabled stanje */

  &:disabled {
    cursor: not-allowed; /* Prikazuje da je dugme onemogućeno */
  }
`;

const ErrorText = styled.p`
  color: var(--error-color);
  font-size: var(--font-size-small);
  margin: var(--spacing-sm) 0 0;
`;

const ProductDiv = styled.div`
  width: 100%;

  max-height: 320px;

  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  margin-bottom: var(--spacing-sm);
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--text-200);
    border-radius: 5px;
  }
`;

const TermsRow = styled(FlexDiv)`
  justify-content: flex-start;
  gap: var(--spacing-xs);
  cursor: pointer;
  margin-top: var(--spacing-md);
`;

const GuidanceBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-base);
  background-color: var(--primary-300);
  color: var(--primary-100);
  font-size: var(--font-size-small);
`;

const Invoice = ({ address, payment, setSubmit, clientSecret }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userProfile, authTokens } = useContext(AuthUserContext);
  const { currencyTag, shippingMethod, createOrder, setCreatedOrder } =
    useContext(ProductContext);
  const [checkbox, setCheckbox] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderError, setOrderError] = useState("");
  const navigate = useNavigate();
  const { goToThankYou } = useNavigation();

  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.discount_price * item.quantity,
    0
  );
  const subtotalRounded = parseFloat(subtotal.toFixed(2));
  const savings = cartItems.reduce(
    (totalSavings, item) =>
      totalSavings +
      (item.product.price - item.product.discount_price) * item.quantity,
    0
  );
  const savingsRounded = parseFloat(savings.toFixed(2));

  const isShippingValid =
    shippingMethod && Object.keys(shippingMethod).length > 0;

  const freeShippingApplies = qualifiesForFreeShipping(
    subtotalRounded,
    shippingMethod?.name
  );

  const shippingCost =
    isShippingValid && !freeShippingApplies ? shippingMethod.price : 0;

  const grandTotal = subtotal + shippingCost;
  const grandTotalRounded = grandTotal.toFixed(2);

  const handleSubmit = async () => {
    if (userProfile && !userProfile.is_email_verified && !checkbox) {
      return;
    }
    setOrderError("");
    setIsSubmitting(true);
    setSubmit(true);
    // Kreiranje objekta sa podacima narudžbine
    const orderData = {
      address: address.id,
      order_items: cartItems.map((item) => ({
        product: item.product.id,
        quantity: item.quantity,
      })),
      payment_method: payment,
      transport_method: shippingMethod.name,
      note: "",
    };
    try {
      // Poziv funkcije za kreiranje narudžbine (pretpostavljamo da funkcija `createAddressBook` postoji)
      const createdOrderCopy = await createOrder(orderData, authTokens.access);
      console.log("Order created successfully", createdOrderCopy);
      setCreatedOrder(createdOrderCopy);
      // Ovde dodajte logiku koja se izvršava nakon uspešnog kreiranja narudžbine
      const totalPrice = cartItems.reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      );
      const currency = localStorage.getItem("currency") || DEFAULT_CURRENCY;

      const items = cartItems.map((item, index) => ({
        item_id: item.product.id,
        item_name: getProductTitle(item.product.category_name, item.product.name),
        affiliation: "Snusco-AT", // Customize as needed
        discount:
          Number(item.product.price) - Number(item.product.discount_price),
        index: index,
        item_category: item.product.category_name,
        item_variant: item.product.nicotine + " MG",
        price: item.product.discount_price,
        quantity: item.quantity,
      }));
      const user_info = jwtDecode(authTokens.access);

      // TagManager.dataLayer({
      //   dataLayer: {
      //     event: "purchase",
      //     transaction_id: `T_${new Date().getTime()}`,
      //     value: totalPrice.toFixed(2),
      //     shipping: Number(shippingMethod.price).toFixed(2),
      //     currency: currency,
      //     items: items,
      //     user_id: user_info.user_id,
      //   },
      // });
      // 1) Isprazni korpu u Redux‑u i localStorage
      dispatch(cartActions.resetCart());
      navigate(goToThankYou());
    } catch (error) {
      console.error("Greška prilikom kreiranja narudžbine:", error); // Obradite grešku
      setOrderError(t("INVOICE.ORDER_ERROR"));
    }
    setIsSubmitting(false);
    setSubmit(false);
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitle>{t("INVOICE.SUMMARY")}</SectionTitle>
      </SectionHeader>
      <SectionBody>
        <ProductDiv>
          {cartItems.map((item, index) => (
            <InvoiceProduct item={item} key={index} />
          ))}
        </ProductDiv>
        <div>
          <FlexDiv>
            <P>{t("INVOICE.SUBTOTAL")}</P>
            <P>
              {currencyTag}
              {subtotalRounded}
            </P>
          </FlexDiv>
          <FlexDiv>
            <P>{shippingMethod.name}</P>
            <P>
              {freeShippingApplies
                ? `🎉 ${t("CART.FREE_SHIPPING_UNLOCKED")}`
                : `${currencyTag}${shippingCost.toFixed(2)}`}
            </P>
          </FlexDiv>
          <FlexDiv>
            <P>{t("CART.VAT")}</P>
            <P>{t("CART.INCLUDED")}</P>
          </FlexDiv>
          <TotalRow>
            <h2>{t("INVOICE.TOTAL_AMOUNT")}</h2>
            <h2>
              {currencyTag}
              {grandTotalRounded}
            </h2>
          </TotalRow>
          {!address && (
            <GuidanceBox>{t("INVOICE.MISSING_ADDRESS")}</GuidanceBox>
          )}
          {address && !payment && (
            <GuidanceBox>{t("INVOICE.MISSING_PAYMENT")}</GuidanceBox>
          )}
          {address && payment && shippingMethod && (
            <>
              <TermsRow onClick={() => setCheckbox(!checkbox)}>
                <svg
                  width="28px"
                  height="28px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
                    stroke="var(--text-200)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="var(--bg-100)"
                  />
                  {checkbox && (
                    <path
                      d="M7.75 12L10.58 14.83L16.25 9.17004"
                      stroke="var(--primary-100)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
                <P style={{ padding: 0 }}>{t("INVOICE.AGREE_TERMS")}</P>
              </TermsRow>
              {payment === paymentMethods[1].code ? (
                // <CreditCardButton
                //   clientSecret={clientSecret}
                //   checkbox={checkbox}
                //   handleOrderSubmission={handleSubmit}
                // />
                <CardPaymentLink
                  amount={grandTotalRounded}
                  email={userProfile.email}
                  checkbox={checkbox}
                />
              ) : (
                <Submit
                  onClick={handleSubmit}
                  disabled={!checkbox || isSubmitting}
                >
                  {t("INVOICE.DELIVER")}
                </Submit>
              )}
              {orderError && <ErrorText>{orderError}</ErrorText>}
            </>
          )}
        </div>
      </SectionBody>
    </SectionCard>
  );
};

export default Invoice;
